/**
 * Chart "Ingresos vs gastos" 12 meses con Recharts.
 *
 * Vista año calendario (C2). Cada mes 2 barras agrupadas:
 * verde = recaudacion, rojo = gastos. Meses futuros muestran barras
 * vacias. El mes en curso (es_parcial) tiene opacidad reducida para
 * que se vea como "datos a hoy, no cerrado".
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PuntoComparativaMensual } from '../../lib/api/dashboard-tesoreria';
import { formatCLP, formatTickCompacto } from '../../lib/format';

const MES_CORTO = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

interface ChartRow {
  mes: string;
  recaudacion: number;
  gastos: number;
  parcial: boolean;
  futuro: boolean;
}

export interface BarChartIngresosVsGastosProps {
  meses: PuntoComparativaMensual[];
  height?: number;
}

export function BarChartIngresosVsGastos({
  meses,
  height = 240,
}: BarChartIngresosVsGastosProps) {
  const rows: ChartRow[] = meses.map((m) => {
    const idx = Number(m.mes.split('-')[1]) - 1;
    return {
      mes: MES_CORTO[idx] ?? m.mes,
      recaudacion: m.recaudacion,
      gastos: m.gastos,
      parcial: m.es_parcial,
      futuro: m.es_futuro,
    };
  });

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
        >
          <CartesianGrid
            stroke="rgba(255,255,255,0.05)"
            strokeDasharray="2 4"
            vertical={false}
          />
          <XAxis
            dataKey="mes"
            tick={{ fill: '#8a8479', fontSize: 11 }}
            axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#8a8479', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatTickCompacto}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
            contentStyle={{
              backgroundColor: '#1a1a1d',
              border: '1px solid #26262a',
              borderRadius: 6,
              fontSize: 12,
              color: '#f4ede0',
            }}
            formatter={(v) => formatCLP(Number(v))}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, color: '#c8c2b6' }}
            iconType="circle"
          />
          <Bar dataKey="recaudacion" name="Recaudación" fill="#5fd9b1" radius={[3, 3, 0, 0]}>
            {rows.map((r, i) => (
              <Cell
                key={`r-${i}`}
                fillOpacity={r.parcial ? 0.6 : r.futuro ? 0.2 : 1}
              />
            ))}
          </Bar>
          <Bar dataKey="gastos" name="Gastos" fill="#ff7a7a" radius={[3, 3, 0, 0]}>
            {rows.map((r, i) => (
              <Cell
                key={`g-${i}`}
                fillOpacity={r.parcial ? 0.6 : r.futuro ? 0.2 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
